import React from "react";
import { Box, Typography, IconButton, Tooltip, Icon } from "@mui/material";
import { toast } from "react-toastify";

interface IdDisplayProps {
  id: string;
  onClick?: () => void;
  onCopy?: (id: string) => void;
  label?: string;
  maxWidth?: number;
  minWidth?: number;
  clickable?: boolean;
}

export const IdDisplay: React.FC<IdDisplayProps> = ({
  id,
  onClick,
  onCopy,
  label = "Copy ID",
  maxWidth = 350,
  minWidth = 130,
  clickable = true,
}) => {
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCopy) {
      onCopy(id);
    } else {
      // Default copy behavior
      navigator.clipboard
        .writeText(id)
        .then(() => {
          toast.success(`ID copied to clipboard: ${id}`);
        })
        .catch(() => {
          toast.error("Failed to copy ID to clipboard");
        });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        cursor: clickable && onClick ? "pointer" : "default",
        minHeight: 40,
        py: 0.5,
        maxWidth,
        minWidth,
        "&:hover":
          clickable && onClick
            ? {
                "& .id-text": {
                  color: "primary.main",
                  textDecoration: "underline",
                },
              }
            : {},
      }}
      onClick={clickable && onClick ? onClick : undefined}
    >
      <Typography
        variant="body2"
        className="id-text"
        sx={{
          fontWeight: 600,
          fontFamily: "monospace",
          transition: "all 0.2s ease",
          wordBreak: "break-all",
          lineHeight: 1.4,
        }}
      >
        {id}
      </Typography>
      <Tooltip title={label} arrow>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            width: 20,
            height: 20,
            opacity: 0.6,
            flexShrink: 0,
            "&:hover": { opacity: 1 },
          }}
        >
          <Icon sx={{ fontSize: 14 }}>content_copy</Icon>
        </IconButton>
      </Tooltip>
    </Box>
  );
};
